import { useEffect, useState } from "react";

interface UseFieldValidationProps {
  fieldType: string;
  value: any;

  // String attributes
  minLength?: number | "";
  maxLength?: number | "";
  regexPattern?: string;

  // Number attributes
  minValue?: number | string | "";
  maxValue?: number | string | "";
  decimalPlaces?: number | "";

  // Time attributes
  timeMinValue?: string;
  timeMaxValue?: string;
}

export const useFieldValidation = ({
  fieldType,
  value,
  minLength,
  maxLength,
  regexPattern,
  minValue,
  maxValue,
  decimalPlaces,
  timeMinValue,
  timeMaxValue,
}: UseFieldValidationProps) => {
  const [validationError, setValidationError] = useState<string>("");

  useEffect(() => {
    // Empty values are considered valid (use IsRequired for required field validation)
    if (!value && value !== false && value !== 0) {
      setValidationError("");
      return;
    }

    let error = "";

    // String field validation
    if (fieldType === "Text" || fieldType === "TextArea") {
      const strValue = String(value);

      if (minLength && strValue.length < minLength) {
        error = `Minimum length is ${minLength} characters`;
      } else if (maxLength && strValue.length > maxLength) {
        error = `Maximum length is ${maxLength} characters`;
      } else if (regexPattern) {
        try {
          const regex = new RegExp(regexPattern);
          if (!regex.test(strValue)) {
            error = "Value does not match the required pattern";
          }
        } catch (e) {
          // Invalid regex - don't set error
        }
      }
    }

    // Number field validation
    if (fieldType === "Number") {
      const strValue = String(value).trim();
      const numValue = parseFloat(strValue);

      if (isNaN(numValue)) {
        error = "Value must be a number";
      } else if (minValue !== "" && numValue < Number(minValue)) {
        error = `Minimum value is ${minValue}`;
      } else if (maxValue !== "" && numValue > Number(maxValue)) {
        error = `Maximum value is ${maxValue}`;
      } else if (decimalPlaces !== "" && decimalPlaces !== undefined) {
        const requiredDecimalPlaces = Number(decimalPlaces);

        const decimalIndex = strValue.indexOf(".");

        if (requiredDecimalPlaces === 0) {
          if (decimalIndex !== -1) {
            error = "Value must be a whole number (no decimal places)";
          }
        } else {
          if (decimalIndex === -1) {
            error = `Value must have exactly ${requiredDecimalPlaces} decimal place${
              requiredDecimalPlaces === 1 ? "" : "s"
            }`;
          } else {
            const actualDecimalPlaces = strValue.length - decimalIndex - 1;
            if (actualDecimalPlaces > requiredDecimalPlaces) {
              error = `Value can have up to${requiredDecimalPlaces} decimal place${
                requiredDecimalPlaces === 1 ? "" : "s"
              }`;
            }
          }
        }
      }
    }

    // Time field validation
    if (fieldType === "Time") {
      const strValue = String(value).trim();

      // Helper function to extract time from ISO string or HH:mm format
      const extractTime = (timeStr: string): string => {
        if (!timeStr) return "";
        // Extract time from ISO string like "1970-01-01T14:30:00"
        const timeMatch = timeStr.match(/T(\d{2}:\d{2})/);
        if (timeMatch) {
          return timeMatch[1]; // Returns "14:30"
        }
        // If already in HH:mm or HH:mm:ss format, extract HH:mm
        if (timeStr.includes(":")) {
          const parts = timeStr.split(":");
          if (parts.length >= 2) {
            return `${parts[0]}:${parts[1]}`;
          }
        }
        return timeStr;
      };

      const normalizedValue = extractTime(strValue);

      if (normalizedValue) {
        // Check if it's a valid time format (HH:mm)
        const timeRegex = /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/;
        if (!timeRegex.test(normalizedValue)) {
          error = "Invalid time format";
        } else {
          // Validate against min and max time
          if (timeMinValue) {
            const normalizedMin = extractTime(timeMinValue);
            if (normalizedMin && normalizedValue < normalizedMin) {
              error = `Time must be at or after ${normalizedMin}`;
            }
          }
          if (!error && timeMaxValue) {
            const normalizedMax = extractTime(timeMaxValue);
            if (normalizedMax && normalizedValue > normalizedMax) {
              error = `Time must be at or before ${normalizedMax}`;
            }
          }
        }
      }
    }

    setValidationError(error);
  }, [
    value,
    fieldType,
    minLength,
    maxLength,
    regexPattern,
    minValue,
    maxValue,
    decimalPlaces,
    timeMinValue,
    timeMaxValue,
  ]);

  return validationError;
};
